import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PersonInput } from '@promanage/contracts';
import { Button, Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { formatRef } from '@promanage/core';
import { EMPTY_PERSON, PersonForm } from '../features/people/PersonForm';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/people/new')({
  component: NewPerson,
});

/**
 * Add a person.
 *
 * THE SAME SHAPE AS `/people/PE000003`: avatar and name in the identity card, Notes in the wide
 * column, Contact down the side. The layout and the validation both live in `PersonForm`, which
 * the edit page also uses — see the note there on why create and edit must not have separate
 * copies of either.
 *
 * The two panels this page adds are the ones it cannot ask for. Roles and relationships attach
 * to a person AFTER they exist, each with its own dates, so they are explained in the place the
 * record keeps them rather than left as a gap somebody has to go looking for.
 */
function NewPerson() {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();

  const create = useMutation(
    trpc.people.create.mutationOptions({
      meta: { errorTitle: 'Could not save that person' },
      onSuccess(person) {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.people.list.queryKey() });
        // Straight to the new record, not back to the list: the next thing anyone does with a
        // person they just added is attach a role to them.
        void navigate({
          to: '/people/$ref',
          params: { ref: formatRef('person', person.refSeq) },
        });
      },
    }),
  );

  const save = (values: PersonInput) => create.mutate(values);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar title="New person" subtitle="Base record — roles are attached separately" />

      <div className="contents" onChange={unsaved.touch}>
        <PersonForm
          initial={EMPTY_PERSON}
          submitLabel="Save person"
          onSubmit={save}
          pending={create.isPending}
          // The server's refusals are written for people — "Someone in this agency already uses
          // that address" — so they are shown as-is. Only an unrecognised shape falls back.
          cancel={
            <Link to="/people">
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </Link>
          }
          mainPanels={
            /*
             * Where "Current roles" sits on the record. An explanation rather than fields,
             * because a role carries its own start date and belongs to the record it points at —
             * put here, every role a person was created with would silently start "today".
             */
            <Panel className="p-5">
              <PanelHeader title="Roles" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                Tenant, occupant, guarantor, trustee, director. Attached after this is saved, from
                the tenancy or owner they point at, so each one carries its own dates — and one
                human stays one record however many hats they wear.
              </p>
            </Panel>
          }
          sidePanels={
            <Panel className="p-5">
              <PanelHeader title="Related people" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                Partner, guardian, next of kin, power of attorney. Added from either person&rsquo;s
                record once both exist — a link needs two ends.
              </p>
            </Panel>
          }
          footnote="Roles and relationships are attached from the records they point at."
        />
      </div>
    </div>
  );
}
